/**
 * Client state for a live game.
 *
 * The server is authoritative, so this store never computes game state — it
 * only records the latest snapshot plus a little presentation state (recent
 * events, connection status). Anything derived is derived from the snapshot.
 */

import { create } from 'zustand';
import type { Card } from '@shared/types/game';
import type { LiveGameState } from '@shared/types/live';
import { isLiveState } from '@shared/types/hearts';
import type { ServerMessage } from '@shared/types/websocket';
import type { ConnectionStatus } from '@/live/socket';
import { cueFor, type FeedbackEvent } from '@/live/cues';

export interface LiveNotice {
  id: number;
  text: string;
}

export interface LiveData {
  code: string | null;
  /** Our own seat, as assigned by the server. */
  seat: number | null;
  state: LiveGameState | null;
  status: ConnectionStatus;
  error: string | null;
  /** The code behind `error`, so a stale one can be retired. */
  errorCode: string | null;
  notices: LiveNotice[];
  standings: Array<{ seat: number; name: string; total: number; placement: number }> | null;
  /**
   * The most recent thing worth hearing. Consumed by `useFeedbackBridge` on
   * object identity — nothing renders from it.
   */
  feedback: FeedbackEvent | null;
}

const EMPTY: LiveData = {
  code: null,
  seat: null,
  state: null,
  status: 'closed',
  error: null,
  errorCode: null,
  notices: [],
  standings: null,
  feedback: null,
};

let noticeId = 0;

/**
 * Errors that stop being true on their own.
 *
 * Only this one so far: everything else — an illegal card, a bad bid — is about
 * what you did and stays until you acknowledge it.
 */
function staleAfterTurnChange(code: string | null, isMyTurn: boolean): boolean {
  return code === 'NOT_YOUR_TURN' && isMyTurn;
}

/**
 * Fold a server message into the store. Pure and exported so the protocol can
 * be tested without a socket or a browser.
 *
 * Informational events only produce notices; the authoritative `state-update`
 * that follows each of them is what actually moves the game on.
 */
export function applyServerMessage(prev: LiveData, message: ServerMessage): Partial<LiveData> {
  const patch = reduceMessage(prev, message);

  // The cue is decided against the state *before* the patch, because
  // `your-turn` is a transition and needs the old turnSeat to compare with.
  const cue = cueFor(prev, message);
  if (!cue) return patch;

  // Id derived from prev rather than a module counter, so tests can assert exact
  // ordering without depending on what ran before them in the same file.
  return { ...patch, feedback: { id: (prev.feedback?.id ?? 0) + 1, cue } };
}

function reduceMessage(prev: LiveData, message: ServerMessage): Partial<LiveData> {
  const notice = (text: string): Partial<LiveData> => ({
    notices: [...prev.notices, { id: ++noticeId, text }].slice(-4),
  });

  const nameOf = (seat: number) => prev.state?.players[seat]?.name ?? `Speler ${seat + 1}`;

  switch (message.type) {
    /*
     * Both stores listen on the same socket shape, so each ignores the other
     * game's snapshots rather than half-reading one into the wrong fields.
     */
    case 'joined':
      if (!isLiveState(message.state)) return {};
      return {
        seat: message.seat,
        state: message.state,
        code: message.state.code,
        error: null,
        errorCode: null,
      };

    case 'state-update': {
      if (!isLiveState(message.state)) return {};

      // Retire an error the new snapshot has made untrue.
      const cleared = staleAfterTurnChange(prev.errorCode, message.state.turnSeat === prev.seat)
        ? { error: null, errorCode: null }
        : {};

      return { state: message.state, code: message.state.code, ...cleared };
    }

    case 'error':
      /*
       * Remember the code, not just the sentence.
       *
       * "Je bent niet aan de beurt" stays on screen until something replaces
       * it, so an impatient tap during someone else's turn leaves that banner
       * sitting there — still visible when your turn arrives and the panel
       * below it says the opposite. Keeping the code lets the next snapshot
       * retire it the moment it stops being true.
       */
      return { error: message.message, errorCode: message.code };

    case 'player-joined':
      return notice(`${message.name} doet mee`);

    case 'player-left':
      return notice(`${nameOf(message.seat)} is de verbinding kwijt`);

    case 'player-reconnected':
      return notice(`${nameOf(message.seat)} is er weer`);

    case 'round-dealt': {
      const cards = `${message.cards} ${message.cards === 1 ? 'kaart' : 'kaarten'}`;
      return {
        ...notice(
          message.trumpSuit === null
            ? `Ronde ${message.roundNo}: ${cards}, troefloos`
            : `Ronde ${message.roundNo}: ${cards}`
        ),
        standings: null,
      };
    }

    case 'trick-won':
      return notice(`${nameOf(message.seat)} pakt de slag`);

    case 'game-ended':
      return { standings: message.standings };

    // Everything below is already visible in the snapshot that follows. The
    // last eight belong to hartenjagen and klaverjassen and reach this reducer
    // only if the wrong store is ever wired to the wrong room — ignored, not
    // crashed on. No comment *between* the labels: eslint's no-fallthrough then
    // treats the group as non-empty and reports every label after it.
    case 'bid-placed':
    case 'all-bids-in':
    case 'card-played':
    case 'round-scored':
    case 'hand-dealt':
    case 'cards-exchanged':
    case 'hand-scored':
    case 'deal-dealt':
    case 'trump-chosen':
    case 'trump-passed':
    case 'deal-scored':
    case 'roem-claimed':
      return {};

    default: {
      const exhaustive: never = message;
      console.warn('unhandled server message', exhaustive);
      return {};
    }
  }
}

interface LiveActions {
  reset: () => void;
  setStatus: (status: ConnectionStatus) => void;
  handle: (message: ServerMessage) => void;
  dismissError: () => void;
}

export const useLive = create<LiveData & LiveActions>()((set) => ({
  ...EMPTY,
  reset: () => set({ ...EMPTY }),
  setStatus: (status) => set({ status }),
  handle: (message) => set((prev) => applyServerMessage(prev, message)),
  dismissError: () => set({ error: null, errorCode: null }),
}));

// --- derived helpers -------------------------------------------------------

export function myPlayer(data: LiveData) {
  if (!data.state || data.seat === null) return null;
  return data.state.players[data.seat] ?? null;
}

export function isMyTurn(data: LiveData): boolean {
  return data.state?.turnSeat !== null && data.state?.turnSeat === data.seat;
}

/** My hand, or an empty list when the server has not dealt to me yet. */
export function myHand(data: LiveData): Card[] {
  return myPlayer(data)?.hand ?? [];
}
